(() => {
  'use strict';
  // Locale strings from layout/theme.liquid (window.abrReviewsStrings); English when missing.
  const abrText = (key, english, values) => {
    const strings = window.abrReviewsStrings || {};
    let text = typeof strings[key] === 'string' && strings[key] ? strings[key] : english;
    if (values) Object.keys(values).forEach((name) => { text = text.split(`[${name}]`).join(String(values[name])); });
    return text;
  };

  if (window.__adobeProductRatingScrollV1) return;
  window.__adobeProductRatingScrollV1 = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const enhanced = new WeakSet();
  let scrollFrame = 0;
  let removeScrollInterruptionListeners = null;
  let stopSettling = null;

  function reviewTarget() {
    return (
      document.querySelector('.jdgm-review-widget .jdgm-rev-widg') ||
      document.querySelector('.jdgm-review-widget') ||
      document.querySelector('#judgeme_product_reviews')
    );
  }

  function headerOffset() {
    let offset = 0;
    const selectors = '[data-sticky-header], .shopify-section-header-sticky, .header-sticky, header';

    document.querySelectorAll(selectors).forEach((header) => {
      const style = window.getComputedStyle(header);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = header.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top <= 8) offset = Math.max(offset, rect.bottom);
    });

    return offset;
  }

  function stopScrollAnimation() {
    stopSettling?.();
    if (scrollFrame) {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = 0;
    }
    if (removeScrollInterruptionListeners) {
      removeScrollInterruptionListeners();
      removeScrollInterruptionListeners = null;
    }
  }

  // targetTop() is re-read on every frame: the page above the reviews
  // keeps changing height while the scroll runs (lazy images loading in
  // as they near the viewport, sections revealing, Judge.me finishing its
  // render), so a destination measured once at the tap was often stale by
  // the time the scroll got there - it stopped short of the reviews.
  function animateScrollTo(targetTop, onComplete) {
    stopScrollAnimation();

    if (reducedMotion.matches) {
      window.scrollTo(0, targetTop());
      settleOn(targetTop, onComplete);
      return;
    }

    const from = window.scrollY;
    const initialDistance = targetTop() - from;
    if (Math.abs(initialDistance) < 1) {
      settleOn(targetTop, onComplete);
      return;
    }

    const duration = Math.min(860, Math.max(560, 460 + Math.abs(initialDistance) * 0.15));
    const startedAt = performance.now();

    const interrupt = () => stopScrollAnimation();
    window.addEventListener('wheel', interrupt, { passive: true });
    window.addEventListener('touchstart', interrupt, { passive: true });
    removeScrollInterruptionListeners = () => {
      window.removeEventListener('wheel', interrupt);
      window.removeEventListener('touchstart', interrupt);
    };

    const frame = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      // Ease-out cubic: the page starts moving on the very first frame
      // after the tap and decelerates into the reviews heading. The old
      // ease-in-out quint moved <1% in its first 20% (~150ms), so tapping
      // the rating looked like nothing happened for a beat.
      const eased = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, from + (targetTop() - from) * eased);

      if (progress < 1) {
        scrollFrame = requestAnimationFrame(frame);
        return;
      }

      scrollFrame = 0;
      if (removeScrollInterruptionListeners) {
        removeScrollInterruptionListeners();
        removeScrollInterruptionListeners = null;
      }
      settleOn(targetTop, onComplete);
    };

    scrollFrame = requestAnimationFrame(frame);
  }

  // After arriving, keep the reviews in place for a moment while anything
  // above them is still loading (it would push them down again), unless
  // the visitor scrolls themselves.
  function settleOn(targetTop, onComplete) {
    let observer = null;
    let timer = 0;
    const interruptions = ['wheel', 'touchstart', 'keydown'];
    const stop = () => {
      observer?.disconnect();
      window.clearTimeout(timer);
      interruptions.forEach((type) => window.removeEventListener(type, stop));
      if (stopSettling === stop) stopSettling = null;
    };
    const hold = () => {
      const top = targetTop();
      if (Math.abs(window.scrollY - top) > 1) window.scrollTo(0, top);
    };
    stopSettling?.();
    stopSettling = stop;
    hold();
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(hold);
      observer.observe(document.body);
    }
    interruptions.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
    timer = window.setTimeout(stop, 1500);
    onComplete?.();
  }

  function scrollToReviews() {
    const target = reviewTarget();
    if (!target) return;

    const top = () => {
      // The widget's own box, not the card inside it: the card rises in
      // with a transform as it scrolls into view, which would move the
      // measured target mid-scroll.
      const current = document.querySelector('.jdgm-review-widget') || reviewTarget() || target;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return Math.min(max, Math.max(0, current.getBoundingClientRect().top + window.scrollY - headerOffset() - 24));
    };
    const heading = target.querySelector('.jdgm-rev-widg__title');

    animateScrollTo(top, () => {
      if (!(heading instanceof HTMLElement)) return;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    });
  }

  function accessibleName(rating) {
    const count = rating.querySelector('.rating-count span:not(.sr-only)')?.textContent?.trim();
    return count ? abrText('read_count', 'Read [reviews]', { reviews: count }) : abrText('read_reviews', 'Read customer reviews');
  }

  function enhance(rating) {
    if (!(rating instanceof HTMLElement) || enhanced.has(rating)) return;
    enhanced.add(rating);
    rating.dataset.abrRatingLink = '';
    rating.setAttribute('role', 'link');
    rating.setAttribute('tabindex', '0');
    rating.setAttribute('aria-label', accessibleName(rating));

    rating.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      scrollToReviews();
    });

    rating.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      scrollToReviews();
    });
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches('.product__rating')) enhance(root);
    root.querySelectorAll?.('.product__rating').forEach(enhance);
  }

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  });

  function start() {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
